import React, { memo, useMemo } from 'react';
import { Col, FormControl, FormLabel, Row } from 'react-bootstrap';
import InputCell from '../services/pipeline/lien-search/InputCell';
import MultiFieldInput from '../services/pipeline/lien-search/MultiFieldInput';
import UtilityServicesTableInput from '../services/pipeline/lien-search/UtilityServicesTableInput';
import OpenInvoicesInput from '../services/pipeline/lien-search/OpenInvoicesInput';
import { ORDER_TYPES } from '../../helpers/constants';
import { applyTaxAmountDueFromBalances, TAX_BALANCE_SUBFIELD_KEYS } from './utils/helpers';

const getFieldColBreakpoints = f => ({
  xs: f.cellSize ?? 6,
  ...(f.cellSizeSm != null ? { sm: f.cellSizeSm } : {}),
  ...(f.cellSizeMd != null ? { md: f.cellSizeMd } : {}),
  ...(f.cellSizeLg != null ? { lg: f.cellSizeLg } : {}),
  ...(f.cellSizeXl != null ? { xl: f.cellSizeXl } : {})
});

const ReportFormGenerator = ({
  orderType,
  defaultValues,
  field,
  fields,
  formValues,
  setFormValues,
  service,
  isFilePopupOpen,
  driveFiles,
  driveFilesLoading,
  onUpdateDriveFiles,
  handleRefresh,
  setConfirmationModal,
  showValidationErrors,
  setValidationStatus,
  onFieldValueChange,
  onMultiFieldChange,
  county,
  enableFieldStatus = false,
  fieldStatuses = null
}) => {
  const resolveFieldTone = fieldKey =>
    enableFieldStatus && fieldStatuses && fieldKey ? fieldStatuses[fieldKey] ?? null : null;

  const documentFileKeys =
    orderType === ORDER_TYPES.ESTOPPEL
      ? ['invoicesFiles', 'hoa1Files', 'hoa2Files', 'hoa3Files']
      : orderType === ORDER_TYPES.SURVEY
      ? ['invoicesFiles']
      : orderType === ORDER_TYPES.LIEN_SEARCH
      ? [
          'invoicesFiles',
          'taxesFiles',
          'permitsFiles',
          'codeIssuesFiles',
          'specialAssessmentsLiensFiles',
          'utilitiesFiles'
        ]
      : [];

  // Calculate used files from all document sections except the current field
  const getUsedFilesForField = currentFieldKey => {
    if (!formValues.documents) return [];

    const allUsedFiles = [];
    const documentKeys = Object.keys(formValues.documents);

    documentKeys.forEach(docKey => {
      // Skip the current field to avoid marking its own files as "used"
      if (docKey !== currentFieldKey && Array.isArray(formValues.documents[docKey])) {
        allUsedFiles.push(...formValues.documents[docKey]);
      }
    });

    return allUsedFiles;
  };

  const shouldShowField = field => {
    if (field.show) {
      if (Array.isArray(field.show)) {
        // all show conditions in the array must be met
        return field.show.every(condition => getValue(condition.key) === condition.value);
      } else {
        // single show condition
        return getValue(field.show.key) === field.show.value;
      }
    }
    if (field.showWhenNot) {
      return getValue(field.showWhenNot.key) !== field.showWhenNot.value;
    }
    return true;
  };

  const isFieldVisible = field => {
    if (field.isVisible) {
      return getValue(field.isVisible.key) === field.isVisible.value;
    }
    return true;
  };

  const getValue = key => {
    // For document file fields, prioritize the documents section
    if (documentFileKeys.includes(key)) {
      // Return from documents section if it exists, otherwise from current field section
      if (formValues.documents && formValues.documents[key] !== undefined) {
        return formValues.documents[key];
      }
    }

    // Check nested or root
    if (formValues[field] && formValues[field][key] !== undefined) {
      return formValues[field][key];
    }
    return formValues[key];
  };

  const getDefaultValue = fieldKey => {
    if (defaultValues[field] && defaultValues[field][fieldKey] && Array.isArray(defaultValues[field][fieldKey])) {
      return { ...defaultValues[field][fieldKey][0] };
    }
    return {};
  };

  const updateValue = (key, value) => {
    // Allow parent to intercept field changes (e.g., for order-specific business logic)
    if (onFieldValueChange) {
      const handled = onFieldValueChange({ field, key, value, formValues, setFormValues, setConfirmationModal });
      if (handled) return;
    }

    const newFormValues = { ...formValues };

    // For all orders, if the key is a document file key, update the documents section
    if (documentFileKeys.includes(key)) {
      // For file fields, ONLY update the documents section
      if (!newFormValues.documents) {
        newFormValues.documents = {};
      }
      newFormValues.documents[key] = value;
    } else {
      // For non-file fields, update the section normally
      if (!newFormValues[field]) {
        newFormValues[field] = {};
      }
      newFormValues[field][key] = value;
    }

    setFormValues(newFormValues);
  };

  const handleMultiFieldChange = (fieldKey, subFieldKey, value, index) => {
    const currentSection = formValues[field] || {};
    const updatedFields = [...(currentSection[fieldKey] || [])];
    let updatedRow = {
      ...updatedFields[index],
      [subFieldKey]: value
    };

    if (onMultiFieldChange) {
      const result = onMultiFieldChange({
        field,
        fieldKey,
        subFieldKey,
        value,
        index,
        row: updatedRow,
        rows: updatedFields
      });
      if (result) {
        updatedRow = result;
      }
    }

    updatedFields[index] = updatedRow;
    let nextSection = {
      ...currentSection,
      [fieldKey]: updatedFields
    };
    if (TAX_BALANCE_SUBFIELD_KEYS.has(subFieldKey)) {
      nextSection = applyTaxAmountDueFromBalances(field, nextSection, fieldKey);
    }
    setFormValues({
      ...formValues,
      [field]: nextSection
    });
  };

  const handleAddSubField = fieldKey => {
    const currentSection = formValues[field] || {};
    const defaultSubField = getDefaultValue(fieldKey);

    const updatedFields = [...(currentSection[fieldKey] || []), defaultSubField];

    const newFormValues = {
      ...formValues,
      [field]: {
        ...currentSection,
        [fieldKey]: updatedFields
      }
    };
    setFormValues(newFormValues);
  };

  const handleDeleteSubField = (fieldKey, index) => {
    const currentSection = formValues[field] || {};
    const updatedFields = [...(currentSection[fieldKey] || [])];
    updatedFields.splice(index, 1);
    const nextSection = applyTaxAmountDueFromBalances(
      field,
      { ...currentSection, [fieldKey]: updatedFields },
      fieldKey
    );
    setFormValues({
      ...formValues,
      [field]: nextSection
    });
  };

  const groupedFields = fields.reduce((acc, field) => {
    if (field?.groupKey) {
      // Remove groupKey from the field
      const { groupKey, ...rest } = field;

      acc[groupKey] = [...(acc[groupKey] || []), rest];
    }
    return acc;
  }, {});

  const renderedGroupKeys = useMemo(() => {
    const seen = new Set();
    return (
      fields
        ?.filter(f => {
          if (!f?.groupKey) return false;
          if (seen.has(f.groupKey)) return false;
          seen.add(f.groupKey);
          return true;
        })
        .map(f => f.groupKey) || []
    );
  }, [fields]);

  return (
    <div>
      <Row className="">
        {fields?.map((f, index) => {
          if (f?.groupKey) {
            if (
              !renderedGroupKeys.includes(f.groupKey) ||
              fields.findIndex(ff => ff.groupKey === f.groupKey) !== index
            ) {
              return null;
            }
            return (
              <Col key={f.groupKey} xs={f?.groupCol || 12} className={`mb-4 card p-2 px-3`}>
                {f?.groupName && <FormLabel className="midFont mb-0">{f?.groupName}</FormLabel>}
                <ReportFormGenerator
                  key={f.key}
                  orderType={orderType}
                  defaultValues={defaultValues}
                  field={field}
                  fields={groupedFields[f.groupKey]}
                  formValues={formValues}
                  setFormValues={setFormValues}
                  service={service}
                  setConfirmationModal={setConfirmationModal}
                  showValidationErrors={showValidationErrors}
                  setValidationStatus={setValidationStatus}
                  county={county}
                  onFieldValueChange={onFieldValueChange}
                  onMultiFieldChange={onMultiFieldChange}
                  enableFieldStatus={enableFieldStatus}
                  fieldStatuses={fieldStatuses}
                />
              </Col>
            );
          } else {
            return !shouldShowField(f) ? null : (
              <React.Fragment key={f.key || `field-${index}`}>
                {f.type === 'radio-with-unit-condo-fields' ? (
                  <Col className={`${f.className || 'mb-4 px-0 '}`} xs={f.cellSize || 6}>
                    <div className={`${f.card ? 'card p-2 px-3' : ''}`}>
                      <Row>
                        <Col xs={12} className="mb-2">
                          <InputCell
                            key={f.key}
                            field={{ ...f, type: 'radio' }}
                            formValues={formValues[field] || {}}
                            onChange={e => updateValue(f.key, e.target.value)}
                            folderId={service?.driveFolder}
                            driveFiles={driveFiles}
                            driveFilesLoading={driveFilesLoading}
                            onUpdateDriveFiles={onUpdateDriveFiles}
                            handleRefresh={handleRefresh}
                            usedFiles={getUsedFilesForField(f.key)}
                            enableFieldStatus={enableFieldStatus}
                            fieldTone={resolveFieldTone(f.key)}
                          />
                        </Col>
                        {getValue(f.key) === 'Yes' && (
                          <Col xs={12} className="mb-2 px-4 pt-2">
                            <Row
                              className="px-2"
                              style={{
                                gap: '14px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start'
                              }}
                            >
                              {/* Unit Column */}
                              <Col className="border p-2 bg-light" style={{ borderRadius: '10px' }}>
                                <div className="w-100 d-flex align-items-center justify-content-between p-1">
                                  <FormLabel className="midFont font-weight-bold mr-2 mb-0">Unit</FormLabel>
                                  <InputCell
                                    field={{
                                      key: f.unitKey,
                                      type: 'radio',
                                      options: ['No', 'Yes']
                                    }}
                                    formValues={{ [f.unitKey]: getValue(f.unitKey) ? 'Yes' : 'No' }}
                                    onChange={e => updateValue(f.unitKey, e.target.value === 'Yes')}
                                    enableFieldStatus={enableFieldStatus}
                                  />
                                </div>
                                {getValue(f.unitKey) && (
                                  <div className="px-1">
                                    <MultiFieldInput
                                      field={{
                                        key: f.unitNumbersKey,
                                        hideLabel: true,
                                        fields: f.fields
                                      }}
                                      formValues={formValues[field] || {}}
                                      onChange={handleMultiFieldChange}
                                      handleDeleteSubField={handleDeleteSubField}
                                      handleAddSubField={handleAddSubField}
                                      isFilePopupOpen={isFilePopupOpen}
                                      enableFieldStatus={enableFieldStatus}
                                      fieldStatuses={fieldStatuses}
                                    />
                                  </div>
                                )}
                              </Col>
                              {/* Condo Column */}
                              <Col className="border p-2 bg-light" style={{ borderRadius: '10px' }}>
                                <div className="w-100 d-flex align-items-center justify-content-between p-1">
                                  <FormLabel className="midFont font-weight-bold mr-2 mb-0">Condo</FormLabel>
                                  <InputCell
                                    field={{
                                      key: f.condoKey,
                                      type: 'radio',
                                      options: ['No', 'Yes']
                                    }}
                                    formValues={{ [f.condoKey]: getValue(f.condoKey) ? 'Yes' : 'No' }}
                                    onChange={e => updateValue(f.condoKey, e.target.value === 'Yes')}
                                    enableFieldStatus={enableFieldStatus}
                                    fieldTone={resolveFieldTone(f.condoKey)}
                                  />
                                </div>
                                {getValue(f.condoKey) && (
                                  <>
                                    <div className="px-1 mb-2">
                                      <InputCell
                                        field={{
                                          key: f.condoInputTypeKey,
                                          type: 'radio',
                                          options: ['Numbers', 'Free Text']
                                        }}
                                        formValues={{
                                          [f.condoInputTypeKey]: getValue(f.condoInputTypeKey) || 'Numbers'
                                        }}
                                        onChange={e => updateValue(f.condoInputTypeKey, e.target.value)}
                                        enableFieldStatus={enableFieldStatus}
                                        fieldTone={resolveFieldTone(f.condoInputTypeKey)}
                                      />
                                    </div>
                                    <div className="px-1">
                                      {getValue(f.condoInputTypeKey) === 'Free Text' ? (
                                        <FormControl
                                          as="textarea"
                                          rows={3}
                                          placeholder="Enter free text description..."
                                          value={
                                            getValue(f.condoInputTypeKey.replace('CondoInputType', 'CondoFreeText')) ||
                                            ''
                                          }
                                          onChange={e =>
                                            updateValue(
                                              f.condoInputTypeKey.replace('CondoInputType', 'CondoFreeText'),
                                              e.target.value
                                            )
                                          }
                                          className="w-100"
                                        />
                                      ) : (
                                        <MultiFieldInput
                                          field={{
                                            key: f.condoNumbersKey,
                                            hideLabel: true,
                                            fields: f.fields
                                          }}
                                          formValues={formValues[field] || {}}
                                          onChange={handleMultiFieldChange}
                                          handleDeleteSubField={handleDeleteSubField}
                                          handleAddSubField={handleAddSubField}
                                          isFilePopupOpen={isFilePopupOpen}
                                          enableFieldStatus={enableFieldStatus}
                                          fieldStatuses={fieldStatuses}
                                        />
                                      )}
                                    </div>
                                  </>
                                )}
                              </Col>
                            </Row>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </Col>
                ) : f.type === 'radio-with-additional-fields' ? (
                  <Col className="" xs={f.cellSize || 6}>
                    <div className={`${f.card ? ' py-2' : ''}`}>
                      <Row>
                        <Col xs={f?.additionalFields ? 6 : 12} className="mb-2">
                          <InputCell
                            key={f.key}
                            field={{ ...f, type: 'radio' }}
                            formValues={formValues[field] || {}}
                            onChange={e => updateValue(f.key, e.target.value)}
                            folderId={service?.driveFolder}
                            driveFiles={driveFiles}
                            driveFilesLoading={driveFilesLoading}
                            onUpdateDriveFiles={onUpdateDriveFiles}
                            handleRefresh={handleRefresh}
                            usedFiles={getUsedFilesForField(f.key)}
                            enableFieldStatus={enableFieldStatus}
                            fieldTone={resolveFieldTone(f.key)}
                          />
                        </Col>
                        {f.additionalFields?.map((additionalField, afIndex) => (
                          <React.Fragment key={additionalField.key || `af-${afIndex}`}>
                            {shouldShowField(additionalField) &&
                              (additionalField.columnGroups ? (
                                <Col xs={additionalField.cellSize || 12} className="mb-2">
                                  <Row>
                                    {additionalField.columnGroups.map((columnGroup, groupIndex) => {
                                      const numColumns = additionalField.columnGroups.length;
                                      const colSize = Math.floor(12 / numColumns); // Distribute columns evenly

                                      return (
                                        <Col key={groupIndex} xs={colSize} className={columnGroup.className || ''}>
                                          {columnGroup.fields?.map(groupField => (
                                            <div key={groupField.key} className="mb-2">
                                              {groupField.type === 'multi-field' ? (
                                                <MultiFieldInput
                                                  field={groupField}
                                                  formValues={formValues[field] || {}}
                                                  onChange={handleMultiFieldChange}
                                                  handleDeleteSubField={handleDeleteSubField}
                                                  handleAddSubField={handleAddSubField}
                                                  isFilePopupOpen={isFilePopupOpen}
                                                  enableFieldStatus={enableFieldStatus}
                                                  fieldStatuses={fieldStatuses}
                                                />
                                              ) : (
                                                <InputCell
                                                  field={groupField}
                                                  formValues={formValues[field] || {}}
                                                  onChange={e => updateValue(groupField.key, e?.target?.value || e)}
                                                  folderId={service?.driveFolder}
                                                  driveFiles={driveFiles}
                                                  driveFilesLoading={driveFilesLoading}
                                                  onUpdateDriveFiles={onUpdateDriveFiles}
                                                  handleRefresh={handleRefresh}
                                                  usedFiles={getUsedFilesForField(groupField.key)}
                                                  enableFieldStatus={enableFieldStatus}
                                                  fieldTone={resolveFieldTone(groupField.key)}
                                                />
                                              )}
                                            </div>
                                          ))}
                                        </Col>
                                      );
                                    })}
                                  </Row>
                                </Col>
                              ) : (
                                <Col className="mb-2" xs={additionalField.cellSize || 6}>
                                  {additionalField.type === 'multi-field' ? (
                                    <MultiFieldInput
                                      key={additionalField.key}
                                      field={additionalField}
                                      formValues={formValues[field] || {}}
                                      onChange={handleMultiFieldChange}
                                      handleDeleteSubField={handleDeleteSubField}
                                      handleAddSubField={handleAddSubField}
                                      isFilePopupOpen={isFilePopupOpen}
                                      enableFieldStatus={enableFieldStatus}
                                      fieldStatuses={fieldStatuses}
                                    />
                                  ) : (
                                    <InputCell
                                      key={additionalField.key}
                                      field={additionalField}
                                      formValues={formValues[field] || {}}
                                      onChange={e => updateValue(additionalField.key, e)}
                                      folderId={service?.driveFolder}
                                      driveFiles={driveFiles}
                                      driveFilesLoading={driveFilesLoading}
                                      onUpdateDriveFiles={onUpdateDriveFiles}
                                      handleRefresh={handleRefresh}
                                      usedFiles={getUsedFilesForField(additionalField.key)}
                                      enableFieldStatus={enableFieldStatus}
                                      fieldTone={resolveFieldTone(additionalField.key)}
                                    />
                                  )}
                                </Col>
                              ))}
                          </React.Fragment>
                        ))}
                      </Row>
                    </div>
                  </Col>
                ) : f.type === 'multi-field' ? (
                  <MultiFieldInput
                    key={f.key}
                    field={f}
                    formValues={formValues[field] || {}}
                    onChange={handleMultiFieldChange}
                    handleDeleteSubField={handleDeleteSubField}
                    handleAddSubField={handleAddSubField}
                    isFilePopupOpen={isFilePopupOpen}
                    enableFieldStatus={enableFieldStatus}
                    fieldStatuses={fieldStatuses}
                  />
                ) : f.type === 'fixed-fields' ? (
                  <Col
                    xs={f.cellSize || 6}
                    {...(f.cellSizeLg != null ? { lg: f.cellSizeLg } : {})}
                    className={f.className || ''}
                  >
                    <Row>
                      {f.fields?.map((subField, subIndex) => (
                        <Col
                          key={subField.key || `fixed-field-${subIndex}`}
                          xs={subField.cellSize || 6}
                          {...(subField.cellSizeMd != null ? { md: subField.cellSizeMd } : {})}
                          {...(subField.cellSizeLg != null ? { lg: subField.cellSizeLg } : {})}
                          className="mb-2"
                        >
                          <InputCell
                            field={subField}
                            formValues={formValues[field] || {}}
                            onChange={e => updateValue(subField.key, e?.target?.value || e)}
                            folderId={service?.driveFolder}
                            driveFiles={driveFiles}
                            driveFilesLoading={driveFilesLoading}
                            onUpdateDriveFiles={onUpdateDriveFiles}
                            handleRefresh={handleRefresh}
                            usedFiles={getUsedFilesForField(subField.key)}
                            enableFieldStatus={enableFieldStatus}
                            fieldTone={resolveFieldTone(subField.key)}
                          />
                        </Col>
                      ))}
                    </Row>
                  </Col>
                ) : f.type === 'section' ? (
                  <Col xs={f.cellSize || 12} className="p-2 px-3">
                    <FormLabel className="midFont mb-0">{f.label}</FormLabel>
                  </Col>
                ) : f.type === 'offset' ? (
                  <Col {...getFieldColBreakpoints({ cellSize: 12, ...f })} style={f.style || {}} />
                ) : f.type === 'open-invoices-input' ? (
                  <OpenInvoicesInput formValues={formValues[field] || {}} onChange={updateValue} />
                ) : f.type === 'utility-services-table-input' ? (
                  <Col xs={f.cellSize || 12} style={{ minWidth: 0, maxWidth: '100%' }}>
                    {enableFieldStatus ? (
                      <div data-field-key={f.key}>
                        <UtilityServicesTableInput
                          key={f.key}
                          formValues={formValues[field] || {}}
                          onChange={e => updateValue(f.key, e)}
                          showValidationErrors={showValidationErrors}
                          setValidationStatus={setValidationStatus}
                          county={county}
                        />
                      </div>
                    ) : (
                      <UtilityServicesTableInput
                        key={f.key}
                        formValues={formValues[field] || {}}
                        onChange={e => updateValue(f.key, e)}
                        showValidationErrors={showValidationErrors}
                        setValidationStatus={setValidationStatus}
                        county={county}
                      />
                    )}
                  </Col>
                ) : (
                  <Col
                    style={{ visibility: isFieldVisible(f) ? 'visible' : 'hidden' }}
                    className={`${f.card ? 'card' : ''} mb-2 ${f.type === 'file' && 'px-0'} ${f.isInDocumentsTrue &&
                      'px-2'} ${f.className || ''}`}
                    {...getFieldColBreakpoints(f)}
                  >
                    <InputCell
                      key={f.key}
                      field={f}
                      formValues={{
                        ...formValues[field],
                        // For file fields, ensure we use the value from documents section
                        ...(f.type === 'file' && { [f.key]: getValue(f.key) })
                      }}
                      onChange={e => updateValue(f.key, e?.target?.value || e)}
                      folderId={service?.driveFolder}
                      driveFiles={driveFiles}
                      driveFilesLoading={driveFilesLoading}
                      onUpdateDriveFiles={onUpdateDriveFiles}
                      handleRefresh={handleRefresh}
                      usedFiles={getUsedFilesForField(f.key)}
                      enableFieldStatus={enableFieldStatus}
                      fieldTone={resolveFieldTone(f.key)}
                    />
                  </Col>
                )}
              </React.Fragment>
            );
          }
        })}
      </Row>
    </div>
  );
};

export default memo(ReportFormGenerator);
